<template>
    <div class="componentRoot">
        <ButtonRowVue>
            <template v-slot:leftSide>
                <v-col :cols="searchBarCols" class="pr-2">
                    <SearchBarVue :labelArray="['basics.name', 'basics.description']" @search="updateSearchQueue" />
                </v-col>
            </template>

            <v-col cols="auto" v-if="$checkRouterPermission('POST')">
                <v-tooltip :text="$t('basics.new', { type: $t('basics.digitalBundle') })">
                    <template v-slot:activator="{ props }">
                        <v-btn v-bind="props" @click="showEditDigitalBundle($event)"
                            class="primaryButton squareButton" flat>
                            <v-icon size="x-large">mdi-plus</v-icon>
                        </v-btn>
                    </template>
                </v-tooltip>
            </v-col>
        </ButtonRowVue>

        <TableVue :numPages="numPages" @switchPage="switchPage" id="digitalBundleTable"
            :resetPage="resetPage" :total="totalCount">
            <thead>
                <tr>
                    <th class="text-left" @click="switchSortBy('name')"
                        :style="{ color: sortBy === 'name' ? 'rgb(var(--v-theme-primary))' : 'unset' }">
                        <div style="display: flex; align-items: center">
                            <b style="font-size: medium">{{ $t('basics.name') }}</b>
                            <div v-if="sortBy === 'name'">
                                <v-icon v-if="!sortDirection">mdi-menu-up</v-icon>
                                <v-icon v-else>mdi-menu-down</v-icon>
                            </div>
                            <div v-else><v-icon>mdi-menu-right</v-icon></div>
                        </div>
                    </th>
                    <th v-if="!isMobile">
                        <b style="font-size: medium">{{ $t('basics.description') }}</b>
                    </th>
                    <th v-if="!isMobile" class="text-left" @click="switchSortBy('price')"
                        :style="{ color: sortBy === 'price' ? 'rgb(var(--v-theme-primary))' : 'unset' }">
                        <div style="display: flex; align-items: center">
                            <b style="font-size: medium">{{ $t('basics.price') }}</b>
                            <div v-if="sortBy === 'price'">
                                <v-icon v-if="!sortDirection">mdi-menu-up</v-icon>
                                <v-icon v-else>mdi-menu-down</v-icon>
                            </div>
                            <div v-else><v-icon>mdi-menu-right</v-icon></div>
                        </div>
                    </th>
                    <th><b style="font-size: medium">{{ $t('basics.digitalMedias') }}</b></th>
                    <th></th>
                </tr>
            </thead>
            <tbody v-if="digitalbundles.length > 0">
                <tr v-for="digitalbundle in digitalbundles" :key="digitalbundle.id"
                    @click="showEditDigitalBundle($event, digitalbundle)">
                    <td>{{ digitalbundle.name }}</td>
                    <td v-if="!isMobile">
                        <span v-if="digitalbundle.description && digitalbundle.description.length > 60">
                            {{ digitalbundle.description.substring(0, 50) }}...
                        </span>
                        <span v-else>{{ digitalbundle.description }}</span>
                    </td>
                    <td v-if="!isMobile">{{ digitalbundle.price }}€</td>
                    <td>{{ digitalbundle.digital_medias?.length ?? 0 }}</td>
                    <td class="text-right">
                        <v-tooltip :text="$t('basics.edit', { type: $t('basics.digitalBundle') })"
                            v-if="$checkRouterPermission('PATCH')">
                            <template v-slot:activator="{ props }">
                                <div @click.stop="showEditDigitalBundle($event, digitalbundle)">
                                    <v-icon v-bind="props">mdi-pencil</v-icon>
                                </div>
                            </template>
                        </v-tooltip>
                    </td>
                </tr>
            </tbody>
            <tbody v-else>
                <tr>
                    <td :colspan="isMobile ? 3 : 5"><i>{{ $t('messages.noItems') }}</i></td>
                </tr>
            </tbody>
        </TableVue>

        <DigitalBundleEditVue :refreshDisplay="editDigitalBundleDrawer" @close="closeEditDigitalBundle"
            :digitalBundleData="activeDigitalBundle" @delete="showDeleteDigitalBundle"
            :readonly="activeDigitalBundle ? !$checkRouterPermission('PATCH') : !$checkRouterPermission('POST')" />

        <DeletionWarningVue type="digitalBundle"
            :additionalMessage="$t('messages.confirmDeletionItem', { item: activeDigitalBundle?.name })"
            :refreshDisplay="deleteDigitalBundleDrawer" @delete="deleteDigitalBundle"
            style="z-index: 1007 !important" />

        <LoadingScreenVue :loading="loading" />
    </div>
</template>

<script>
import DigitalBundleEditVue from '../../../components/products/DigitalBundleEdit.vue';

export default {
    name: 'digitalBundleList',
    components: { DigitalBundleEditVue },
    data() {
        return {
            digitalbundles: [],
            activeDigitalBundle: undefined,
            editDigitalBundleDrawer: 0,
            deleteDigitalBundleDrawer: 0,
            currPage: 1,
            numPages: 0,
            totalCount: 0,
            pageSize: 0,
            resetPage: 0,
            searchQueue: '',
            sortBy: 'name',
            sortDirection: true,
            loading: false,
            requestId: 0,
        };
    },
    computed: {
        isMobile() {
            return ['xs', 'sm'].includes(this.$vuetify.display.name);
        },
        searchBarCols() {
            switch (this.$vuetify.display.name) {
                case 'xs': return 9;
                case 'sm': return 6;
                case 'xxl': return 3;
                default: return 4;
            }
        },
    },
    methods: {
        showError(error) {
            console.log(error);
            window.dispatchEvent(new CustomEvent('showErrorDisplay', { detail: error }));
        },
        async fetchData() {
            const id = ++this.requestId;
            this.loading = true;
            try {
                const { data } = await this.axios.get(
                    this.axios.defaults.baseURL + 'media/digitalbundles/',
                    {
                        params: {
                            page: this.currPage,
                            order: this.sortBy,
                            direction: this.sortDirection ? 'up' : 'down',
                            query: this.searchQueue,
                        },
                    }
                );
                if (id !== this.requestId) return;
                this.digitalbundles = data.results;
                this.totalCount = data.count;
                if (this.currPage === 1) this.pageSize = data.results.length;
                this.numPages = this.pageSize ? Math.ceil(data.count / this.pageSize) : 0;
            } catch (error) {
                if (id === this.requestId) this.showError(error);
            } finally {
                if (id === this.requestId) this.loading = false;
            }
        },
        showEditDigitalBundle(event, digitalbundle) {
            event.preventDefault();
            event.stopPropagation();
            this.activeDigitalBundle = digitalbundle;
            this.editDigitalBundleDrawer++;
        },
        closeEditDigitalBundle(saved) {
            this.activeDigitalBundle = undefined;
            this.editDigitalBundleDrawer = 0;
            if (saved) this.fetchData();
        },
        switchPage(page) {
            this.currPage = page;
            this.fetchData();
        },
        updateSearchQueue(value) {
            this.resetPage++;
            this.currPage = 1;
            this.searchQueue = value;
            this.fetchData();
        },
        switchSortBy(value) {
            if (this.sortBy !== value) {
                this.sortBy = value;
                this.sortDirection = true;
            } else {
                this.sortDirection = !this.sortDirection;
            }
            this.currPage = 1;
            this.resetPage++;
            this.fetchData();
        },
        showDeleteDigitalBundle() {
            this.deleteDigitalBundleDrawer++;
        },
        async deleteDigitalBundle() {
            if (!this.activeDigitalBundle) return;
            this.loading = true;
            try {
                await this.axios.patch(
                    this.axios.defaults.baseURL + 'media/digitalbundles/delete/' + this.activeDigitalBundle.id,
                    { deleted: true }
                );
                this.deleteDigitalBundleDrawer = 0;
                this.editDigitalBundleDrawer = 0;
                this.activeDigitalBundle = undefined;
                this.currPage = 1;
                this.resetPage++;
                await this.fetchData();
            } catch (error) {
                this.showError(error);
            } finally {
                this.loading = false;
            }
        },
    },
    mounted() {
        this.fetchData();
    },
};
</script>
